// src/pages/SystemAnalytics.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Activity,
  BarChart3,
  Cpu,
  Database,
  Monitor,
  RefreshCw,
  Server,
  TrendingUp,
  TrendingDown,
  Users,
  DollarSign,
  BookOpen,
  AlertCircle,
  CheckCircle,
  Clock,
  Zap,
} from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import { systemAnalyticsService } from "../services/system-analytics";
import {
  SystemHealth,
  SystemAnalytics,
  UserActivityAnalytics,
  SystemPerformance,
  AnalyticsTimeframe,
} from "../types/system-analytics";

const SystemAnalyticsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [timeframe, setTimeframe] = useState<AnalyticsTimeframe>("30d");

  // State for different data types
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [systemAnalytics, setSystemAnalytics] =
    useState<SystemAnalytics | null>(null);
  const [userActivity, setUserActivity] =
    useState<UserActivityAnalytics | null>(null);
  const [systemPerformance, setSystemPerformance] =
    useState<SystemPerformance | null>(null);

  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAllData();
  }, [timeframe]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSystemHealth(),
        loadSystemAnalytics(),
        loadUserActivity(),
        loadSystemPerformance(),
      ]);
    } catch (error) {
      // Silently handle error as individual functions will show toasts
    } finally {
      setLoading(false);
    }
  };

  const loadSystemHealth = async () => {
    try {
      const response = await systemAnalyticsService.getSystemHealth();
      if (response && typeof response === "object" && "data" in response) {
        setSystemHealth(response.data);
      } else {
        setSystemHealth(response as SystemHealth);
      }
    } catch (error: any) {
      toast.error(
        `Failed to load system health: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const loadSystemAnalytics = async () => {
    try {
      const response = await systemAnalyticsService.getSystemAnalytics(
        timeframe
      );
      if (response && typeof response === "object" && "data" in response) {
        setSystemAnalytics(response.data);
      } else {
        setSystemAnalytics(response as SystemAnalytics);
      }
    } catch (error: any) {
      toast.error(
        `Failed to load system analytics: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const loadUserActivity = async () => {
    try {
      const response = await systemAnalyticsService.getUserActivityAnalytics(
        timeframe
      );
      if (response && typeof response === "object" && "data" in response) {
        setUserActivity(response.data);
      } else {
        setUserActivity(response as UserActivityAnalytics);
      }
    } catch (error: any) {
      toast.error(
        `Failed to load user activity: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const loadSystemPerformance = async () => {
    try {
      const response = await systemAnalyticsService.getSystemPerformance();
      if (response && typeof response === "object" && "data" in response) {
        setSystemPerformance(response.data);
      } else {
        setSystemPerformance(response as SystemPerformance);
      }
    } catch (error: any) {
      toast.error(
        `Failed to load system performance: ${
          error.response?.data?.message || error.message
        }`
      );
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAllData();
    setRefreshing(false);
    toast.success("Data refreshed successfully");
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${days}d ${hours}h ${minutes}m`;
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case "healthy":
        return "text-green-600 bg-green-100";
      case "warning":
        return "text-yellow-600 bg-yellow-100";
      case "critical":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
    }).format(amount);
  };

  if (
    loading &&
    !systemHealth &&
    !systemAnalytics &&
    !userActivity &&
    !systemPerformance
  ) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
                <Activity className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  System Analytics
                </h1>
                <p className="text-slate-600 font-semibold mt-1">
                  Monitor system health, performance, and usage
                </p>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-center h-96">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12 text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-blue-500 mx-auto mb-6"></div>
              <span className="text-xl font-bold text-slate-900 block mb-2">
                Loading system analytics...
              </span>
              <p className="text-sm text-slate-500 font-semibold">
                This may take a few seconds
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-4 shadow-lg">
                <Activity className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-slate-900">
                  System Analytics
                </h1>
                <p className="text-slate-600 font-semibold mt-1">
                  Monitor system health, performance, and usage
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <select
                value={timeframe}
                onChange={(e) =>
                  setTimeframe(e.target.value as AnalyticsTimeframe)
                }
                className="bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl px-4 py-3 font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent"
              >
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
                <option value="1y">Last year</option>
              </select>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2"
              >
                <RefreshCw
                  className={`w-5 h-5 text-blue-500 ${
                    refreshing ? "animate-spin" : ""
                  }`}
                />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* System Health Overview */}
        {systemHealth && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center">
                <div
                  className={`p-3 rounded-2xl shadow-lg ${
                    systemHealth.status === "healthy"
                      ? "bg-gradient-to-br from-emerald-300 to-emerald-400"
                      : systemHealth.status === "warning"
                      ? "bg-gradient-to-br from-amber-300 to-amber-400"
                      : "bg-gradient-to-br from-red-300 to-red-400"
                  }`}
                >
                  {systemHealth.status === "healthy" ? (
                    <CheckCircle className="w-6 h-6 text-white" />
                  ) : systemHealth.status === "warning" ? (
                    <AlertCircle className="w-6 h-6 text-white" />
                  ) : (
                    <AlertCircle className="w-6 h-6 text-white" />
                  )}
                </div>
                <div className="ml-4">
                  <p className="text-sm font-bold text-slate-600">
                    System Status
                  </p>
                  <p className="text-2xl font-bold capitalize text-slate-900">
                    {systemHealth.status}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center">
                <div className="p-3 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl shadow-lg">
                  <Clock className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-bold text-slate-600">Uptime</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {formatUptime(systemHealth.system?.uptime || 0)}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center">
                <div
                  className={`p-3 rounded-2xl shadow-lg ${
                    (systemHealth.system?.memory?.memoryUsage || 0) > 80
                      ? "bg-gradient-to-br from-red-300 to-red-400"
                      : (systemHealth.system?.memory?.memoryUsage || 0) > 60
                      ? "bg-gradient-to-br from-amber-300 to-amber-400"
                      : "bg-gradient-to-br from-emerald-300 to-emerald-400"
                  }`}
                >
                  <Monitor className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-bold text-slate-600">
                    Memory Usage
                  </p>
                  <p className="text-2xl font-bold text-slate-900">
                    {systemHealth.system?.memory?.memoryUsage || 0}%
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
              <div className="flex items-center">
                <div
                  className={`p-3 rounded-2xl shadow-lg ${
                    systemHealth.database?.healthy
                      ? "bg-gradient-to-br from-emerald-300 to-emerald-400"
                      : "bg-gradient-to-br from-red-300 to-red-400"
                  }`}
                >
                  <Database className="w-6 h-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-bold text-slate-600">Database</p>
                  <p className="text-2xl font-bold text-slate-900 capitalize">
                    {systemHealth.database?.status || "Unknown"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl mb-6 overflow-hidden">
          <nav className="flex">
            {[
              { id: "overview", label: "Overview", icon: BarChart3 },
              { id: "performance", label: "Performance", icon: Zap },
              { id: "users", label: "User Activity", icon: Users },
              { id: "system", label: "System Health", icon: Server },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-4 px-6 font-bold text-sm transition-all duration-300 ${
                  activeTab === tab.id
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg"
                    : "text-slate-600 hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50"
                }`}
              >
                <tab.icon className="w-5 h-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === "overview" && systemAnalytics && (
            <SystemOverview
              analytics={systemAnalytics}
              formatCurrency={formatCurrency}
            />
          )}

          {activeTab === "overview" && !systemAnalytics && (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
              <div className="text-center">
                <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
                  <BarChart3 className="w-12 h-12 text-white" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">
                  No analytics data available. Please try refreshing.
                </p>
              </div>
            </div>
          )}

          {activeTab === "performance" && systemPerformance && (
            <SystemPerformanceView performance={systemPerformance} />
          )}

          {activeTab === "performance" && !systemPerformance && (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
              <div className="text-center">
                <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
                  <Zap className="w-12 h-12 text-white" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">
                  No performance data available. Please try refreshing.
                </p>
              </div>
            </div>
          )}

          {activeTab === "users" && userActivity && (
            <UserActivityView activity={userActivity} />
          )}

          {activeTab === "users" && !userActivity && (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
              <div className="text-center">
                <div className="bg-gradient-to-br from-blue-300 to-cyan-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
                  <Users className="w-12 h-12 text-white" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">
                  No user activity data available. Please try refreshing.
                </p>
              </div>
            </div>
          )}

          {activeTab === "system" && systemHealth && (
            <SystemHealthView
              health={systemHealth}
              formatUptime={formatUptime}
            />
          )}

          {activeTab === "system" && !systemHealth && (
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
              <div className="text-center">
                <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
                  <Server className="w-12 h-12 text-white" />
                </div>
                <p className="text-slate-500 font-semibold text-lg">
                  No system health data available. Please try refreshing.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// System Overview Component
const SystemOverview: React.FC<{
  analytics: SystemAnalytics;
  formatCurrency: (amount: number) => string;
}> = ({ analytics, formatCurrency }) => {
  // Safety checks for data
  if (!analytics) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
        <div className="text-center">
          <div className="bg-gradient-to-br from-slate-300 to-slate-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
            <BarChart3 className="w-12 h-12 text-white" />
          </div>
          <p className="text-slate-500 font-semibold text-lg">
            Loading analytics data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl shadow-lg">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-bold text-slate-600">Active Users</p>
              <p className="text-3xl font-bold text-slate-900">
                {analytics.user?.activeUsers || 0}
              </p>
              <p className="text-sm text-slate-500 font-semibold">
                of {analytics.user?.totalUsers || 0} total
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl shadow-lg">
              <DollarSign className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-bold text-slate-600">Revenue</p>
              <p className="text-3xl font-bold text-slate-900">
                {formatCurrency(analytics.financial?.totalRevenue || 0)}
              </p>
              <div className="flex items-center text-sm">
                {(analytics.growth?.revenueGrowth || 0) >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-500 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span
                  className={
                    (analytics.growth?.revenueGrowth || 0) >= 0
                      ? "text-emerald-600 font-bold"
                      : "text-red-600 font-bold"
                  }
                >
                  {(analytics.growth?.revenueGrowth || 0) > 0 ? "+" : ""}
                  {analytics.growth?.revenueGrowth || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-purple-300 to-purple-400 rounded-2xl shadow-lg">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-bold text-slate-600">Students</p>
              <p className="text-3xl font-bold text-slate-900">
                {analytics.academic?.totalStudents || 0}
              </p>
              <div className="flex items-center text-sm">
                {(analytics.growth?.studentGrowth || 0) >= 0 ? (
                  <TrendingUp className="w-4 h-4 text-emerald-500 mr-1" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
                )}
                <span
                  className={
                    (analytics.growth?.studentGrowth || 0) >= 0
                      ? "text-emerald-600 font-bold"
                      : "text-red-600 font-bold"
                  }
                >
                  {(analytics.growth?.studentGrowth || 0) > 0 ? "+" : ""}
                  {analytics.growth?.studentGrowth || 0}%
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:scale-105 transition-all duration-300">
          <div className="flex items-center">
            <div className="p-3 bg-gradient-to-br from-pink-300 to-rose-400 rounded-2xl shadow-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-bold text-slate-600">Activities</p>
              <p className="text-3xl font-bold text-slate-900">
                {analytics.activity?.totalActivities || 0}
              </p>
              <p className="text-sm text-slate-500 font-semibold">
                in selected period
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// System Performance Component
const SystemPerformanceView: React.FC<{
  performance: SystemPerformance;
}> = ({ performance }) => {
  if (!performance) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
        <div className="text-center">
          <div className="bg-gradient-to-br from-purple-300 to-indigo-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
            <Zap className="w-12 h-12 text-white" />
          </div>
          <p className="text-slate-500 font-semibold text-lg">
            Loading performance data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Memory Usage */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Memory Usage</h3>
            <div className="p-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl shadow-md">
              <Monitor className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                Heap Used
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.memory?.heapUsed || 0} MB
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                Heap Total
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.memory?.heapTotal || 0} MB
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">RSS</span>
              <span className="text-sm font-bold text-slate-900">
                {performance.memory?.rss || 0} MB
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                External
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.memory?.external || 0} MB
              </span>
            </div>
          </div>
        </div>

        {/* CPU Usage */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">CPU Usage</h3>
            <div className="p-2 bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl shadow-md">
              <Cpu className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                Total
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.cpu?.percent || 0}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">User</span>
              <span className="text-sm font-bold text-slate-900">
                {performance.cpu?.user || 0} ms
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                System
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.cpu.system} ms
              </span>
            </div>
          </div>
        </div>

        {/* Database */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-slate-900">Database</h3>
            <div className="p-2 bg-gradient-to-br from-purple-300 to-purple-400 rounded-xl shadow-md">
              <Database className="w-6 h-6 text-white" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                Status
              </span>
              <span className="text-sm font-bold text-slate-900 capitalize">
                {performance.database.connectionStatus}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                Connections
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.database.activeConnections}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-semibold text-slate-600">
                Slow Queries
              </span>
              <span className="text-sm font-bold text-slate-900">
                {performance.database.slowQueries}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// User Activity Component
const UserActivityView: React.FC<{
  activity: UserActivityAnalytics;
}> = ({ activity }) => {
  if (!activity) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
        <div className="text-center">
          <div className="bg-gradient-to-br from-blue-300 to-cyan-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
            <Users className="w-12 h-12 text-white" />
          </div>
          <p className="text-slate-500 font-semibold text-lg">
            Loading user activity data...
          </p>
        </div>
      </div>
    );
  }

  const roleData = (activity.activeUsersByRole || []).map((role) => ({
    name: role._id,
    value: role.count,
    fill: `hsl(${Math.random() * 360}, 70%, 50%)`,
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Active Users by Role */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl shadow-md">
              <Users className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Active Users by Role
            </h3>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={roleData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {roleData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Activity Summary */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl shadow-md">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Activity Summary
            </h3>
          </div>
          <div className="space-y-4">
            {activity.activeUsersByRole.map((role, index) => (
              <div
                key={role._id}
                className="flex items-center justify-between p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl"
              >
                <div className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded-full shadow-md"
                    style={{ backgroundColor: `hsl(${index * 60}, 70%, 50%)` }}
                  ></div>
                  <span className="text-sm font-bold text-slate-700 capitalize">
                    {role._id}
                  </span>
                </div>
                <span className="text-sm text-slate-900 font-bold">
                  {role.count} users
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// System Health Component
const SystemHealthView: React.FC<{
  health: SystemHealth;
  formatUptime: (seconds: number) => string;
}> = ({ health, formatUptime }) => {
  if (!health) {
    return (
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
        <div className="text-center">
          <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-6 w-fit mx-auto mb-4 shadow-lg">
            <Server className="w-12 h-12 text-white" />
          </div>
          <p className="text-slate-500 font-semibold text-lg">
            Loading system health data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* System Information */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-blue-300 to-indigo-400 rounded-xl shadow-md">
              <Server className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              System Information
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Platform
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.system?.memory?.platform || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Architecture
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.system?.memory?.arch || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Node Version
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.system?.nodeVersion || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Environment
              </span>
              <span className="text-sm font-bold text-slate-900 capitalize">
                {health.system?.environment || "N/A"}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Uptime
              </span>
              <span className="text-sm font-bold text-slate-900">
                {formatUptime(health.system?.uptime || 0)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Hostname
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.system?.memory?.hostname || "N/A"}
              </span>
            </div>
          </div>
        </div>

        {/* Database Health */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gradient-to-br from-emerald-300 to-green-400 rounded-xl shadow-md">
              <Database className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-xl font-bold text-slate-900">
              Database Health
            </h3>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Status
              </span>
              <span
                className={`text-sm font-bold px-3 py-1.5 rounded-xl shadow-md text-white ${
                  health.database?.healthy
                    ? "bg-gradient-to-r from-emerald-300 to-emerald-400"
                    : "bg-gradient-to-r from-red-300 to-red-400"
                }`}
              >
                {health.database?.status || "Unknown"}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Response Time
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.database?.responseTime || 0}ms
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Users
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.database?.collections?.users || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Branches
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.database?.collections?.branches || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Students
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.database?.collections?.students || 0}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl">
              <span className="text-sm font-semibold text-slate-600">
                Teachers
              </span>
              <span className="text-sm font-bold text-slate-900">
                {health.database?.collections?.teachers || 0}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemAnalyticsDashboard;
